import React, { useState, useContext } from 'react';
import { Typography } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { setNewDefaultAnalytics } from '../../../utils/address-analytics';
import { getAddressErrorCode } from '../../../utils/address';
import { EDITED_ADDRESS_MSG } from '../../../constants/response-success-messages';
import { Alert } from '../../shared/alert/alert';
import { FORM_MODE, IMappedAddress } from '../../../models/common/addresses';
import { getLastModifiedDate } from '../../../utils/date';
import { getHyphenatedPhoneNumber, scrollToTop } from '../../../utils/general';
import { MeatballsMenu } from '../../shared/meatballs-menu/meatballs-menu';
import { addressesApi } from '../../../api/common/addresses';
import { useAddresses } from '../../../hooks/use-addresses';
import { AddressParagraph } from '../address-paragraph/address-paragraph';
import { TErrorMessage } from '../../../models/common/alert-messages';
import {
  ERR_PRIMARY_PHONE_REQUIRED,
  ERR_UNABLE_TO_SAVE,
} from '../../../constants/response-error-messages';
import { DeleteAddressModal } from '../delete-address-modal/delete-address-modal';
import { DeleteDefaultAddressModal } from '../delete-address-modal/delete-default-address';
import { DefaultAddressModal } from '../default-address-modal/default-address-modal';
import { useModal } from '../../../hooks/use-modal';
import { pushErrorEvent, setOverlayEvent } from '../../../utils/analytics';
import { PO_BOX_ADDRESS_REGEX } from '../../../constants/regex';

interface IAddressCardProps {
  index: number;
  address: IMappedAddress;
  onDeleteSuccess: (index: number) => void;
  onDeleteFailure: (index: number) => void;
  onCloseSuccessMsg: () => void;
  resetActionsState: () => void;
  isShowEditedSuccessMsgMW: boolean;
  onDefaultChangeSuccess: (index: number) => void;
  onDefaultChangeFailure: (index: number) => void;
}

export const AddressCard = ({
  index,
  address,
  onDeleteSuccess,
  onDeleteFailure,
  resetActionsState,
  onCloseSuccessMsg,
  isShowEditedSuccessMsgMW,
  onDefaultChangeFailure,
  onDefaultChangeSuccess,
}: IAddressCardProps) => {
  const [errorMessage, setErrorMessage] = useState<TErrorMessage | null>(null);
  const [phoneError, setPhoneError] = useState<TErrorMessage | null>(null);
  const { openModal, hideModal, setComponentProps } = useModal();
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const modalSize = channel === 'tablet' ? 'md' : 'sm';
  const isPoBoxAddress = PO_BOX_ADDRESS_REGEX.test(address.addressLine1 || '');
  const { setAddressFormMode, setEditableAddress, changeDefault, isB2B } =
    useAddresses();
  const {
    isDefault,
    companyName,
    primaryPhone,
    alternatePhone,
    addedBy,
    lastModifiedDate,
    firstName,
    lastName,
    nickName,
  } = address;

  const lastEditedDate = lastModifiedDate
    ? getLastModifiedDate(+lastModifiedDate)
    : '';
  const phoneNumber = getHyphenatedPhoneNumber(primaryPhone || '');
  const alternatePhoneNumber = getHyphenatedPhoneNumber(alternatePhone || '');

  const openDeleteModal = () => {
    if (!isDefault) {
      openModal(DeleteAddressModal, {
        address,
        onConfirmDelete: deleteAddress,
        isLoading: false,
      });
      setOverlayEvent('are you sure you want to delete address');
    } else {
      openModal(
        DeleteDefaultAddressModal,
        {
          address,
        },
        {
          size: modalSize,
        },
      );
      setOverlayEvent('cant delete default address');
    }
  };

  const showEditForm = () => {
    resetActionsState();
    setAddressFormMode(FORM_MODE.EDIT);
    scrollToTop();
    setEditableAddress(address);
    setOverlayEvent('edit address');
  };

  const deleteAddress = () => {
    setComponentProps({ isLoading: true });
    addressesApi
      .deleteAddress(isB2B, address)
      .then(() => {
        onDeleteSuccess(index);
      })
      .catch(() => {
        setErrorMessage(ERR_UNABLE_TO_SAVE);
        pushErrorEvent(
          'address card: delete',
          'inline',
          'n/a',
          ERR_UNABLE_TO_SAVE.message,
        );
        onDeleteFailure(index);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
        hideModal();
      });
  };

  const openDefaultConfirmModal = () => {
    if (!isDefault) {
      openModal(DefaultAddressModal, {
        address,
        onSave: changeDefaultAddress,
        isLoading: false,
      });
    }
    setNewDefaultAnalytics();
  };

  const changeDefaultAddress = () => {
    setPhoneError(null);
    if (!primaryPhone) {
      setPhoneError(ERR_PRIMARY_PHONE_REQUIRED);
      pushErrorEvent(
        'address card: set as default',
        'inline',
        'n/a',
        ERR_PRIMARY_PHONE_REQUIRED.message,
      );
      hideModal();
      return;
    }
    setComponentProps({ isLoading: true });
    changeDefault(address.addrIdentifier)
      .then(() => {
        onDefaultChangeSuccess(index);
      })
      .catch((error) => {
        setErrorMessage(ERR_UNABLE_TO_SAVE);
        pushErrorEvent(
          'address card: set as default',
          'inline',
          getAddressErrorCode(error),
          ERR_UNABLE_TO_SAVE.message,
        );
        onDefaultChangeFailure(index);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
        hideModal();
      });
  };

  return (
    <>
      {phoneError && <Alert status="error">{phoneError.message}</Alert>}
      {errorMessage && isMobile && (
        <Alert status="error">{errorMessage.message}</Alert>
      )}
      {isShowEditedSuccessMsgMW && isMobile && (
        <div className="sui-mb-4">
          <Alert
            status="success"
            onClose={onCloseSuccessMsg}
            data-testid={EDITED_ADDRESS_MSG.dataTestId}
          >
            {EDITED_ADDRESS_MSG.message}
          </Alert>
        </div>
      )}
      <div
        data-testid="addressDataContainer"
        className={`sui-w-full sui-p-4 sui-bg-primary sui-rounded-base sui-border-solid
        ${
          isDefault
            ? 'sui-border-2 sui-border-accent sui-gap-1'
            : 'sui-border-1 sui-border-inactive'
        }`}
      >
        {isDefault && (
          <Typography weight="bold" color="subtle">
            Default Address
          </Typography>
        )}
        <div className="sui-flex sui-justify-between sui-justify-center">
          <Typography
            className="sui-text-3xl sui-mr-4 sui-leading-loose"
            variant="h3"
            weight="bold"
            data-qm-mask=""
            truncate
          >
            {firstName} {lastName}
          </Typography>
          <div>
            <MeatballsMenu
              label="Address"
              items={[
                {
                  value: 'Edit',
                  action: showEditForm,
                  dataTestId: 'edit-address-meatball',
                },
                {
                  value: 'Set as Default Address',
                  action: openDefaultConfirmModal,
                  isHidden: isDefault || isPoBoxAddress,
                  dataTestId: 'set-default-address-meatball',
                },
                {
                  value: 'Delete',
                  action: openDeleteModal,
                  dataTestId: 'delete-address-meatball',
                },
              ]}
            />
          </div>
        </div>
        {nickName && (
          <Typography variant="body-lg" color="subtle" truncate>
            {nickName}
          </Typography>
        )}
        <div className="sui-my-4 sui-leading-tight">
          {companyName && (
            <Typography data-testid={`addressCompanyContainer_${index}`}>
              <span className="sui-line-clamp-2">{companyName}</span>
            </Typography>
          )}
          <AddressParagraph address={address} />
          {phoneNumber && (
            <Typography
              data-qm-mask=""
              data-testid={`addressPhContainer_${index}`}
            >
              {phoneNumber}
            </Typography>
          )}
          {alternatePhoneNumber && (
            <Typography
              data-qm-mask=""
              data-testid={`addressPhContainerTwo_${index}`}
            >
              {alternatePhoneNumber}
            </Typography>
          )}
        </div>
        {addedBy && (
          <Typography color="subtle" height="tight">
            Added by <span data-qm-mask="">{addedBy}</span>
          </Typography>
        )}
        <Typography color="subtle" height="tight">
          Last edited {lastEditedDate}
        </Typography>
      </div>
    </>
  );
};

AddressCard.displayName = 'AddressCard';
